import { Camera, Info } from "lucide-react";
import type { HomePageData } from "../../services/HomeService";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import {
  gluceraBody,
  gluceraBrandSolidBlueButton,
  gluceraHeroTitle,
  gluceraMicroLabel,
  gluceraNanoLabel,
  gluceraSectionTitle,
  gluceraStatNano,
} from "../../utils/gluceraTypography";
import { homeAccentChip, homeAccentText, homeTipsBody, homeTipsLabel, homeTipsShell } from "../../utils/homeBlueTheme";
import { getSuggestedMealContext } from "../../utils/homePageUtils";
import HomePoweringPredictionList from "./HomePoweringPredictionList";

export interface NoMealTodayHomeSectionProps {
  onLogMeal: () => void;
  onRecalibrate?: () => void;
  homeData?: HomePageData;
  isToday?: boolean;
}

const NoMealTodayHomeSection = ({
  onLogMeal,
  onRecalibrate,
  homeData,
  isToday = true,
}: NoMealTodayHomeSectionProps) => {
  const mealCtx = getSuggestedMealContext();
  const poweringItems = homeData?.["Powering This Prediction"] ?? [];

  return (
    <div className="space-y-4 pb-2">
      {!isToday ? (
        <>
          <Card className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
            <div className="h-1 bg-gradient-to-r from-muted via-border to-muted" />
            <CardContent className="space-y-3 p-5">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 ring-1 ring-border/60",
                  gluceraNanoLabel
                )}
              >
                No data
              </span>
              <h2 className={gluceraHeroTitle}>No meals logged this day.</h2>
              <p className={gluceraBody}>
                No meal was recorded for this date. Log meals daily to track your
                glucose patterns over time.
              </p>
            </CardContent>
          </Card>

          {poweringItems.length > 0 && (
            <HomePoweringPredictionList
              items={poweringItems}
              headerLabel="Context from this date"
            />
          )}
        </>
      ) : (
        <>
          <div className="space-y-3">
            <h2 className={gluceraHeroTitle}>You haven&apos;t logged today.</h2>
            <p className={cn(gluceraBody, "italic")}>
              This is not a failure state — it is the next decision point.
            </p>
            <div className="flex flex-wrap gap-2">
              <span
                className={cn(
                  "inline-flex rounded-full px-2.5 py-1",
                  gluceraNanoLabel,
                  homeAccentChip
                )}
              >
                @ {mealCtx.chip}
              </span>
              <span
                className={cn(
                  "inline-flex rounded-full px-2.5 py-1",
                  gluceraNanoLabel,
                  homeAccentChip
                )}
              >
                1 min photo
              </span>
            </div>
          </div>

          <Card className="overflow-hidden rounded-2xl border-2 border-[#737DFE]/20 bg-card shadow-sm dark:border-[#737DFE]/30">
            <CardContent className="space-y-4 p-4 xs:p-5">
              <div className="flex items-center justify-between gap-2">
                <span className={gluceraMicroLabel}>Today&apos;s first action</span>
                <span
                  className={cn(
                    "rounded-md px-2 py-0.5",
                    gluceraStatNano,
                    homeAccentChip
                  )}
                >
                  Meal gap
                </span>
              </div>
              <div className="space-y-2">
                <h3 className={gluceraSectionTitle}>
                  {mealCtx.meal} usually spikes. Quick photo?
                </h3>
                <p className={gluceraBody}>
                  Your last three {mealCtx.meal.toLowerCase()}es averaged a{" "}
                  {mealCtx.peakHint} mg/dL predicted peak. Log this one before or
                  right after eating to see the next 2 hours.
                </p>
              </div>
              <div className={cn("flex gap-2.5 rounded-xl border p-3", homeTipsShell)}>
                <Info className={cn("mt-0.5 h-4 w-4 shrink-0", homeAccentText)} />
                <p className={cn("text-xs leading-relaxed", homeTipsBody)}>
                  <span className={cn("font-bold uppercase tracking-wide", homeTipsLabel)}>
                    Pattern:{" "}
                  </span>
                  Croissant or rice at lunch creates your highest peaks. Salad-first
                  usually saves 16 mg/dL.
                </p>
              </div>
              <Button
                type="button"
                onClick={onLogMeal}
                className={cn("h-12 w-full gap-2 rounded-2xl text-sm font-semibold shadow-md", gluceraBrandSolidBlueButton)}
              >
                <Camera className="h-5 w-5 shrink-0" strokeWidth={2.25} />
                Log {mealCtx.meal.toLowerCase()}
              </Button>
            </CardContent>
          </Card>

          {poweringItems.length > 0 && (
            <HomePoweringPredictionList
              items={poweringItems}
              headerLabel="You can update"
              headerHint="Improves your next prediction"
              onRecalibrate={onRecalibrate}
            />
          )}
        </>
      )}
    </div>
  );
};

export default NoMealTodayHomeSection;
